/**
 * YAML 1.2 core-schema scalars: how plain text becomes null, a boolean, a number or a
 * string, and the reverse question for writing YAML — when a string must be quoted so
 * that a reader does not turn it into something else.
 */

import type { DataValue } from "@/lib/tool-kit/latex-dev/data-model";

const RE_NULL = /^(?:null|Null|NULL|~)$/;
const RE_BOOL = /^(?:true|True|TRUE|false|False|FALSE)$/;
const RE_INT = /^[-+]?[0-9]+$/;
const RE_OCTAL = /^0o[0-7]+$/;
const RE_HEX = /^0x[0-9a-fA-F]+$/;
const RE_FLOAT = /^[-+]?(?:\.[0-9]+|[0-9]+(?:\.[0-9]*)?)(?:[eE][-+]?[0-9]+)?$/;
const RE_INFINITY = /^([-+]?)\.(?:inf|Inf|INF)$/;
const RE_NAN = /^\.(?:nan|NaN|NAN)$/;

/** Words YAML 1.1 reads as booleans; YAML 1.2 reads them as text. */
const RE_YAML11_BOOL =
  /^(?:y|Y|yes|Yes|YES|n|N|no|No|NO|on|On|ON|off|Off|OFF)$/;

/** Forms YAML 1.1 reads as numbers or dates that 1.2 reads as text. */
const RE_YAML11_NUMBER =
  /^(?:[-+]?0[0-7_]+|[-+]?[0-9][0-9_]*[0-9]|0b[01_]+|[-+]?[0-9][0-9_]*(?::[0-5]?[0-9])+(?:\.[0-9_]*)?|[0-9]{4}-[0-9]{1,2}-[0-9]{1,2}.*)$/;

export interface ResolvedScalar {
  value: DataValue;
  kind: "null" | "bool" | "int" | "float" | "str";
  /** A YAML 1.1 reader would see something else here (yes, no, on, off). */
  yaml11?: boolean;
  /** `.inf` or `.nan`: no JSON form. */
  nonFinite?: boolean;
}

function toInteger(text: string): number | bigint {
  const big = BigInt(text.startsWith("+") ? text.slice(1) : text);
  const num = Number(big);
  return Number.isSafeInteger(num) ? num : big;
}

/** Resolve a plain (unquoted) scalar with the core schema. */
export function resolvePlain(text: string): ResolvedScalar {
  if (text === "" || RE_NULL.test(text)) {
    return { value: null, kind: "null" };
  }
  if (RE_BOOL.test(text)) {
    return { value: text.toLowerCase() === "true", kind: "bool" };
  }
  if (RE_INT.test(text) || RE_OCTAL.test(text) || RE_HEX.test(text)) {
    return { value: toInteger(text), kind: "int" };
  }
  if (RE_FLOAT.test(text)) {
    return { value: Number(text), kind: "float" };
  }
  const infinity = RE_INFINITY.exec(text);
  if (infinity) {
    const negative = infinity[1] === "-";
    return {
      value: negative ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY,
      kind: "float",
      nonFinite: true,
    };
  }
  if (RE_NAN.test(text)) {
    return { value: Number.NaN, kind: "float", nonFinite: true };
  }
  return RE_YAML11_BOOL.test(text)
    ? { value: text, kind: "str", yaml11: true }
    : { value: text, kind: "str" };
}

/** True when text read as a plain scalar would not come back as the same string. */
export function needsQuotes(text: string): boolean {
  const resolved = resolvePlain(text);
  return (
    typeof resolved.value !== "string" ||
    resolved.yaml11 === true ||
    RE_YAML11_NUMBER.test(text) ||
    text === "=" ||
    text === "<<"
  );
}

/** Convert a mapping key to the string JSON needs. */
export function keyToString(value: DataValue): string | null {
  if (typeof value === "string") {
    return value;
  }
  if (value === null) {
    return "null";
  }
  if (typeof value === "number" || typeof value === "bigint") {
    return String(value);
  }
  return typeof value === "boolean" ? String(value) : null;
}

const INDICATORS = new Set([
  "-",
  "?",
  ":",
  ",",
  "[",
  "]",
  "{",
  "}",
  "#",
  "&",
  "*",
  "!",
  "|",
  ">",
  "'",
  '"',
  "%",
  "@",
  "`",
]);

const RE_NEEDS_ESCAPE = /[\u{7F}-\u{9F}\u{2028}\u{2029}\u{FEFF}]/gu;

/** Characters a YAML reader may treat as breaks or drop: controls, NEL, LS, PS, BOM. */
export function hasSpecialCharacter(
  text: string,
  allowLineFeed = false
): boolean {
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const control = code < 32 && !(allowLineFeed && code === 10) && code !== 9;
    if (
      control ||
      (code >= 127 && code <= 159) ||
      code === 0x20_28 ||
      code === 0x20_29 ||
      code === 0xfe_ff
    ) {
      return true;
    }
  }
  return false;
}

/** True when a string can be written as an unquoted single-line scalar. */
export function isPlainSafe(text: string): boolean {
  if (text === "" || needsQuotes(text)) {
    return false;
  }
  if (
    text !== text.trim() ||
    hasSpecialCharacter(text) ||
    text.includes("\n") ||
    text.includes("\t")
  ) {
    return false;
  }
  if (text.startsWith("---") || text.startsWith("...")) {
    return false;
  }
  const first = text[0];
  if (INDICATORS.has(first)) {
    const next = text[1];
    const dashLike = first === "-" || first === "?" || first === ":";
    if (!dashLike || next === undefined || next === " " || next === "\t") {
      return false;
    }
  }
  return !(text.includes(": ") || text.includes(" #") || text.endsWith(":"));
}

function escapeChar(ch: string): string {
  return `\\u${ch.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0")}`;
}

/** A double-quoted YAML string (JSON escapes, plus the characters YAML treats as breaks). */
export function doubleQuote(text: string): string {
  const json = JSON.stringify(text);
  return json.replace(RE_NEEDS_ESCAPE, (ch) => escapeChar(ch));
}
